import axios from 'axios';
// eslint-disable-next-line import/no-extraneous-dependencies
import { fr, nl } from 'date-fns/locale';
import type { Locale } from '../types/gatsby';

// Switch to Reviews.io when there are more than 2 reviews.
export const REVIEWS_IO_THRESHOLD = 2;

export type Sort = 'date_desc' | 'date_asc' | 'highest_rating' | 'lowest_rating';

export interface ProductReviews {
  average_rating: number;
  review_count: number;
  results_count: number;
  ratings: Record<string, number>;
  reviews: Review[];
  reviewsWithImages: Review[];
}

export interface ReviewReply {
  comments: string;
  date_created: string;
  author: {
    name: string;
  }
}

export interface Review {
  id: string;
  rating: number;
  title: string;
  comments: string;
  helpful_count: number;
  date_created: string;
  product_name?: string;
  photos: string[];
  author: {
    name: string;
    location: string;
  }
  replies: ReviewReply[];
}

/**
 * Locale mapping for the reviews.io API
 */
const localeMapping: Record<Locale, 'nl' | 'fr'> = {
  'nl-NL': 'nl',
  'nl-BE': 'nl',
  'fr-BE': 'fr',
};

/**
 * Locale mapping for date-fns
 */
export const dateLocalesMapping = {
  'nl-NL': nl,
  'nl-BE': nl,
  'fr-BE': fr,
};

interface ReviewResponse {
  average_rating: number;
  review_count: number;
  results_count: number;
  stats: {
    ratings: Record<string, number>;
  };
  reviews: Review[];
}

/**
 * Fetch product reviews from reviews.io
 * @param productKey
 * @param locale
 * @param page
 * @param sort
 * @param stars
 */
export const fetchProductReviews = async ({
  contentfulId, locale, page = 0, sort = 'date_desc', stars,
}: {
  contentfulId: string;
  locale: Locale;
  page?: number;
  stars?: number[];
  sort?: Sort;
}) => {
  const { data } = await axios.get<ReviewResponse>('https://api.reviews.io/reviews', {
    params: {
      store: String(process.env.GATSBY_STORE).replace('-', '.'),
      sku: contentfulId,
      tag: localeMapping[locale],
      type: 'product_review',
      must_have_body: false,
      per_page: 2,
      minRating: stars?.length ? Math.min(...stars) : 1,
      page: page || 0,
      sort,
    },
  });

  return data;
};
